import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { getRebuildAPI } from "@rebuild/shared/api";
import type { Estimation } from "@/interfaces/estimation.interface";
import { ScopeNoteSource } from "@rebuild/shared";
import { ESTIMATE_KEYS } from "@/hooks/queries/estimates/queryKeys";
import { JOB_KEYS } from "@/hooks/queries/jobs/queryKeys";
import { dispatch } from "@/redux/store";
import { estimationActions } from "@/redux/actions/estimation.actions";
import type { AddNoteBody } from "@rebuild/shared/generated/models/addNoteBody";
import { DOCUSKETCH_PLAYER_URL_REGEX } from "@/components/estimation-details/upload/addDocumentationDialog.utils";

const { estimatesControllerAddNote } = getRebuildAPI();

export interface AddDocumentationParams {
  estimateId: string;
  estimation: Estimation;
  scopeNoteText?: string;
  scopeNoteWorkAfterHours?: boolean;
  userId?: string;
  docusketchLink?: string;
}

export function useAddDocumentationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: AddDocumentationParams) => {
      const { estimateId, scopeNoteText, scopeNoteWorkAfterHours, userId, docusketchLink } = params;

      if (!scopeNoteText && !docusketchLink) {
        return { newScopeNoteObject: undefined, hadDocusketchLink: false };
      }

      const payload: AddNoteBody = {
        uuid: uuidv4(),
        dateCreated: new Date().toISOString(),
      };

      if (scopeNoteText) {
        payload.text = scopeNoteText;
        payload.source = ScopeNoteSource.UPLOADED;
        payload.createdByUserId = userId;
        payload.workAfterHours = scopeNoteWorkAfterHours;
      }

      if (docusketchLink) {
        payload.docusketchLink = docusketchLink;
      }

      await estimatesControllerAddNote(estimateId, payload);

      const { docusketchLink: _, ...scopeNoteData } = payload;
      return { newScopeNoteObject: scopeNoteText ? scopeNoteData : undefined, hadDocusketchLink: !!docusketchLink };
    },
    onSuccess: (_data, variables) => {
      // A scope note / docusketch link was submitted (vs. a files-only upload, which the server
      // tracks via BullMQ jobs, not scopeNoteProcessingStatus, and short-circuits above).
      const startedDocumentationProcessing = !!variables.scopeNoteText || !!variables.docusketchLink;

      const updatedEstimation: Estimation = {
        ...variables.estimation,
        // The server has already flipped this to "processing". Set it optimistically so the
        // estimate view drops back into its loading state immediately, before the metadata
        // refetch lands. The reprocessing poll in useEstimateDetail then tracks it through to
        // "complete" and refetches the generated line items.
        ...(startedDocumentationProcessing ? { scopeNoteProcessingStatus: "processing" as const } : {}),
        scopeNoteObjects: _data.newScopeNoteObject
          ? [...(variables.estimation.scopeNoteObjects ?? []), _data.newScopeNoteObject]
          : variables.estimation.scopeNoteObjects,
      };

      if (variables.docusketchLink) {
        const docusketchId = variables.docusketchLink.match(DOCUSKETCH_PLAYER_URL_REGEX)?.[1];
        if (docusketchId) {
          updatedEstimation.docusketchId = docusketchId;
          updatedEstimation.links = [...(variables.estimation.links ?? []), variables.docusketchLink];
        }
      }

      dispatch(
        estimationActions.setEstimationState({
          estimation: updatedEstimation,
        }),
      );

      queryClient.invalidateQueries({
        queryKey: ESTIMATE_KEYS.detail(variables.estimateId),
      });
      queryClient.invalidateQueries({
        queryKey: ESTIMATE_KEYS.detailMetadata(variables.estimateId),
      });
      queryClient.invalidateQueries({
        queryKey: JOB_KEYS.forEstimate(variables.estimateId),
      });

      const message = _data.hadDocusketchLink
        ? "Documentation added. 3D scan images and notes are being processed..."
        : "Documentation added. This may take a few minutes to appear in the estimate.";

      toast.success(message, {
        className: "!text-green-500 !bg-green-500/20 !border-green-500",
      });
    },
    onError: (error) => {
      toast.error("Error adding documentation. Our team has been notified.", {
        className: "!text-red-500 !bg-red-500/10 !border-red-500",
      });
      console.error("Add documentation error:", error);
    },
  });
}
